import type { ReactNode } from "react";
import { useDisclosure } from "@/components/ui/Disclosure";
import {
  ReferenceWords,
  type ReferenceSource,
} from "@/components/ui/StructuredText";
import { Button } from "@/components/ui/button";
import { plural } from "@/lib/plural";

/** The fields an element is listed by, as the invention serves it. */
export interface ListedElement {
  element_id: string;
  number: number | null;
  text: string;
}

/** The fields a claim is listed by, as the claim strategy serves it. */
export interface ListedClaim {
  claim_id: string;
  number: number;
  kind: "independent" | "dependent";
  parent_claim_id: string | null;
  lead_in: string | null;
  lines: {
    text: string;
    depth: number;
    /** The words in their stretches, each with the drawn part it names, where served. */
    runs?: { text: string; reference?: string | null }[];
  }[];
}

/** What a screen shows beside the parts a claim names: a numeral, and what it says. */
export type ClaimReferences = Pick<ReferenceSource, "tagOf" | "markOf">;

/**
 * The elements of the invention as the attorney confirmed them, numbered. An element awaiting
 * attention has no number and is not yet part of what the plan must support.
 */
export function ElementList({ elements }: { elements: ListedElement[] }) {
  const numbered = elements.filter((element) => element.number !== null);
  if (numbered.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No element is confirmed yet.
      </p>
    );
  }
  return (
    <ol className="space-y-1 text-sm">
      {numbered.map((element) => (
        <li key={element.element_id} className="flex gap-2">
          <span className="font-mono text-xs text-primary">
            {element.number}
          </span>
          <span>{element.text}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * A claim's words as one passage, each named stretch with its numeral beside it.
 *
 * The numeral is the attribute the shared reference style draws after the words,
 * so the words themselves are never changed. A line with no numeral to show, a
 * line served without its stretches, or a list given no ``references`` reads as
 * its words alone.
 */
function Words({
  claim,
  references,
}: {
  claim: ListedClaim;
  references?: ClaimReferences;
}) {
  const parts: ReactNode[] = [];
  const add = (node: ReactNode) => {
    if (parts.length > 0) parts.push(" ");
    parts.push(node);
  };
  if (claim.lead_in) add(claim.lead_in);
  claim.lines.forEach((line, at) => {
    const runs = line.runs ?? [];
    const named = (reference: string | null | undefined): reference is string =>
      references !== undefined &&
      Boolean(reference) &&
      references.tagOf(reference ?? "") !== null;
    if (references === undefined || !runs.some((run) => named(run.reference))) {
      add(line.text);
      return;
    }
    add(
      <span key={`${String(at)}:${line.text}`}>
        {runs.map((run, i) =>
          named(run.reference) ? (
            <ReferenceWords
              key={`${String(i)}:${run.text}`}
              reference={run.reference}
              references={references}
              tinted={false}
            >
              {run.text}
            </ReferenceWords>
          ) : (
            run.text
          ),
        )}
      </span>,
    );
  });
  return <>{parts}</>;
}

function words(claim: ListedClaim): string {
  return [claim.lead_in, ...claim.lines.map((line) => line.text)]
    .filter(Boolean)
    .join(" ");
}

/**
 * A claim's title, as the framework names it: its preamble, up to the first colon, semicolon
 * or ", comprising", cut to about 80 characters at a word.
 */
export function claimTitle(claim: ListedClaim): string {
  const preamble = (words(claim).split(/[:;]|,\s*comprising/i)[0] ?? "")
    .replace(/\s+comprising$/i, "")
    .trim();
  return preamble.length > 80
    ? `${preamble.slice(0, 78).replace(/\s+\S*$/, "")}…`
    : preamble;
}

function Independent({
  claim,
  dependents,
  references,
}: {
  claim: ListedClaim;
  dependents: ListedClaim[];
  references?: ClaimReferences;
}) {
  const fold = useDisclosure(false);
  return (
    <li className="space-y-1">
      <p className="flex gap-2">
        <span className="font-mono text-xs text-primary">{claim.number}</span>
        <span>
          <Words claim={claim} references={references} />
        </span>
      </p>
      {dependents.length > 0 && (
        <>
          <Button {...fold.trigger} variant="quiet" size="dense">
            {`${fold.open ? "Hide" : "Show"} ${plural(dependents.length, "dependent claim")} of claim ${String(claim.number)}`}
          </Button>
          <ol {...fold.panel} className="space-y-1 pl-6">
            {dependents.map((dependent) => (
              <li key={dependent.claim_id} className="flex gap-2">
                <span className="font-mono text-xs text-muted-foreground">
                  {dependent.number}
                </span>
                <span>
                  <Words claim={dependent} references={references} />
                </span>
              </li>
            ))}
          </ol>
        </>
      )}
    </li>
  );
}

/** Every claim beneath an independent claim, at any depth, in the set's order. */
function beneath(claims: ListedClaim[], claimId: string): ListedClaim[] {
  const found: ListedClaim[] = [];
  const frontier = [claimId];
  for (const claim of claims) {
    if (
      claim.parent_claim_id !== null &&
      frontier.includes(claim.parent_claim_id)
    ) {
      found.push(claim);
      frontier.push(claim.claim_id);
    }
  }
  return found;
}

/** The claim set: each independent claim, with its dependents folded beneath it. */
export function ClaimList({
  claims,
  references,
}: {
  claims: ListedClaim[];
  /** Where a screen shows numerals beside the parts a claim names; none by default. */
  references?: ClaimReferences;
}) {
  if (claims.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No claim is drafted yet.</p>
    );
  }
  return (
    <ol className="space-y-2 text-sm">
      {claims
        .filter((claim) => claim.kind === "independent")
        .map((claim) => (
          <Independent
            key={claim.claim_id}
            claim={claim}
            dependents={beneath(claims, claim.claim_id)}
            references={references}
          />
        ))}
    </ol>
  );
}
